import React, { useState, useEffect } from "react";
import PageTitle from "../components/PageTitle";
import { Link, useParams } from "react-router-dom";
import TenantLink from "../components/TenantLink";
import useTenantNavigate from "../hooks/useTenantNavigate";
import { useAxiosPost } from "../hooks/useAxios";
import { ServiceType } from "../utils/enums";
import { motion } from "framer-motion";
import {
  Badge,
  Breadcrumb,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Popover,
} from "antd";
import {
  FaArrowUpRightFromSquare,
  FaArrowsRotate,
  FaArrowRightFromBracket,
} from "react-icons/fa6";
import {
  FaAngleDoubleDown,
  FaAngleDown,
  FaAngleUp,
  FaPlus,
} from "react-icons/fa";
import UniversalLoader from "../components/UniversalLoader";
import FetchNoticesImg from "../assets/images/FETCHNOTICES.jpg";
import { toast } from "react-toastify";
import WarningImage from "../assets/images/Warning.png";
import moment from "moment";
import {
  clientActivation,
  createNotice,
  deleteDocument,
  fetchData,
  getNoticeDetails,
  postData,
  saveClientDetails,
  saveNotice,
  updateData,
  deleteData,
} from "../api/apiService";
import {
  DeleteOutlined,
  DeleteRowOutlined,
  EditOutlined,
  EditTwoTone,
  SyncOutlined,
} from "@ant-design/icons";
import { getClientDocType } from "../utils/helpers";
import { MdOutlineDisabledByDefault } from "react-icons/md";
import EditableDateInput from "../components/EditableDateInput";
import DashboardButton from "../components/DashboardButton";
import FetchNoticesConfirmModal from "../components/FetchNoticesConfirmModal";
import WhitebooksSessionCard from "../components/WhitebooksSessionCard";
import { CLIENTS, LAMBDA, EVENTS, DOCUMENTS } from "../api/endpointMap";

const urlMapping = {
  fetchClients: {
    [ServiceType.GSTIN]: CLIENTS.GST_DETAILS,
    [ServiceType.INCOME_TAX]: CLIENTS.INCOME_TAX_DETAILS,
    [ServiceType.TDS]: CLIENTS.TDS_DETAILS,
  },
  syncNotices: {
    [ServiceType.GSTIN]: LAMBDA.PROCESS_SELECTED_CLIENTS,
    // IT: single-client endpoint (semantically correct for ClientView page).
    // Backend reads `client` field from payload (syncClientNotices sends both
    // `client` and `client_names` for cross-page compatibility).
    [ServiceType.INCOME_TAX]: LAMBDA.IT_FETCH_NOTICES_SINGLE,
    [ServiceType.TDS]: EVENTS.TDS_PROCESS_SELECTED,
  },
  clientLogin: {
    [ServiceType.GSTIN]: EVENTS.GST_LOGIN,
    [ServiceType.INCOME_TAX]:
      EVENTS.INCOME_TAX_LOGIN,
    [ServiceType.TDS]: EVENTS.TDS_LOGIN,
  },
};

export const ClientView = ({ serviceType }) => {
  const navigate = useTenantNavigate();
  const { clientId } = useParams();

  const [loaderText, setLoaderText] = useState("Fetching Notices");
  const [formData, setFormData] = useState({});
  const [responseOutstandingsCount, setResponseOutstandingsCount] = useState(0);
  const [eproceedingsCount, setEproceedingsCount] = useState(0);
  const [saveClientLoading, setSaveClientLoading] = useState(false);
  const [editingCredentials, setEditingCredentials] = useState(false);

  const [gstNoticesCount, setGstNoticesCount] = useState(0);
  const [additionalNoticesCount, setAdditionalNoticesCount] = useState(0);

  const [tdsNoticeCount, setTDSNoticesCount] = useState(0);
  const [syncNoticeLoading, setSyncNoticesLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [fetchNoticeModal, setFetchNoticeModal] = useState(false);
  const [initiateLoginModal, setInitiateLoginModal] = useState(false);
  const [initiateLDeleteModal, setInitiateDeleteModal] = useState(false);
  const [disableClientModal, setDisableClientModal] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState(formData);
  const [deleteClientLoading, setDeleteClientLoading] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  // Whitebooks inline-retry state — set when a GST fetch attempt
  // discovers there's no live Whitebooks session for this client.
  // The Modal that consumes this state renders a WhitebooksSessionCard
  // pointed at the right client; on successful OTP, we auto-retry
  // the original fetch (preserving the `force` flag).
  const [whitebooksRetryClientId, setWhitebooksRetryClientId] = useState(null);
  const [whitebooksRetryForce, setWhitebooksRetryForce] = useState(false);

  const [fetchNoticeCounts] = useAxiosPost(DOCUMENTS.GET_COUNT);

  const [syncClientNoticesData] = useAxiosPost(
    urlMapping.syncNotices[serviceType]
  );

  const [syncClientLoginData] = useAxiosPost(
    urlMapping.clientLogin[serviceType]
  );

  const getClientApiBase = () => {
    const map = {
      "income-tax": "/api/v1/clients/income-tax",
      gstin: "/api/v1/clients/gst",
      tds: "/api/v1/clients/tds",
    };
    return map[serviceType];
  };

  const handleDeleteClient = async () => {
    try {
      setDeleteClientLoading(true);

      await deleteData(`${getClientApiBase()}/${clientId}`);

      toast.info("Deleted Client Successfully");
      navigate(-1);
    } catch (err) {
      toast.error("Please Delete Linked Notices First");
    } finally {
      setDeleteClientLoading(false);
    }
  };

  const handleDisableclient = async () => {
    setSaveClientLoading(true);

    try {
      await updateData(`${getClientApiBase()}/${clientId}`, {
        disabled: !formData.disabled,
      });
      toast.info("Client Info Updated Successfully");
      setInitialFormData(formData);
      setIsFormChanged(false);

      fetchClientDetails();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update client");
    } finally {
      setSaveClientLoading(false);
    }
  };

  // Fetch notice count for a given doctype via existing list endpoints.
  // Uses page_length=1 so only total_records is fetched (no payload).
  const getNoticesCount = async (docType) => {
    if (!clientId) return;

    // Map Frappe doctype → FastAPI endpoint + state setter
    const countMap = {
      "E Proceeding": {
        url: "/api/v1/income-tax/e-proceedings",
        setter: setEproceedingsCount,
      },
      "Response to Outstanding Demand": {
        url: "/api/v1/income-tax/demands",
        setter: setResponseOutstandingsCount,
      },
      "GST Notice And Order": {
        url: "/api/v1/gst/notices",
        setter: setGstNoticesCount,
      },
      "GST Additional Notice And Order": {
        url: "/api/v1/gst/additional-notices",
        setter: setAdditionalNoticesCount,
      },
      "Quarter": {
        url: "/api/v1/tds/financial-notices",
        setter: setTDSNoticesCount,
      },
    };

    const entry = countMap[docType];
    if (!entry) return;

    try {
      // For TDS, the "TDS Notices" listing this badge links to shows flattened
      // per-quarter rows (include_quarters=true), so the badge must count
      // quarters too — otherwise one FY with N quarters shows badge=1 while the
      // list shows N rows. The backend returns the quarter count as
      // total_records only when include_quarters is set.
      const params = { client: clientId, page_length: 1 };
      if (entry.url.includes("/tds/financial-notices")) {
        params.include_quarters = true;
      }
      const data = await fetchData(entry.url, params);
      const total = data?.data?.total_records ?? 0;
      entry.setter(total);
    } catch (err) {
      // Silent fail — badge just stays at 0
      console.error(`Failed to fetch count for ${docType}:`, err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === "disabled" ? (checked ? 1 : 0) : value,
    });
  };

  const fetchClientDetails = async () => {
    try {
      const fetchUrlMap = {
        "income-tax": "/api/v1/clients/income-tax",
        gstin: "/api/v1/clients/gst",
        tds: "/api/v1/clients/tds",
      };
      const baseUrl = fetchUrlMap[serviceType];
      if (!baseUrl) return;

      const clientsResponse = await fetchData(`${baseUrl}/${clientId}`);
      setInitialFormData(clientsResponse || {});
      setFormData(clientsResponse || {});
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to load client details");
    }
  };

  const syncClientNotices = async (data, force = false) => {
    setSyncNoticesLoading(true);
    const portalType = serviceType === "gstin" ? "gst" : serviceType === "tds" ? "tds" : "income_tax";
    const ids = Array.isArray(data) ? data : [data];

    // For GST clients, pre-flight the Whitebooks session state if the
    // method is whitebooks_api. Without this, the BE will return a 409
    // mid-fetch and `useAxios` would surface only a generic toast
    // (it stringifies errors). The pre-flight lets us open the inline
    // OTP modal proactively — same UX, no toast noise.
    if (serviceType === "gstin" && ids[0]) {
      try {
        const raw = await fetchData(LAMBDA.GST_CLIENT_WHITEBOOKS_STATUS(ids[0]));
        // BE wraps tenant GST responses as {message, success, data}
        // (gst.py `_response()`), NOT as {result} like admin pages.
        // Read `.data` first; fall back to `.result` then the raw body
        // so this doesn't break if the envelope ever changes.
        const s = raw?.data || raw?.result || raw || {};
        if (s.method_active && !(s.session && s.session.ready)) {
          // Whitebooks is the active fetch method and this client has
          // no live session. Open the inline OTP modal pointed at this
          // client — the WhitebooksSessionCard already supports being
          // mounted inline. Once the session goes ready, the user can
          // re-click Fetch.
          setSyncNoticesLoading(false);
          setWhitebooksRetryClientId(ids[0]);
          setWhitebooksRetryForce(force);
          return;
        }
      } catch (preflightErr) {
        // Don't hard-fail the fetch on a pre-flight failure — let the
        // BE be the source of truth. Just log and continue.
        console.warn("Whitebooks pre-flight check failed:", preflightErr);
      }
    }

    const payload = {
      // `client` is the single-endpoint field; `client_names` kept for the
      // batch-endpoint code paths (GST still goes to the batch-shaped URL).
      client: ids[0],
      client_names: ids,
      portal_type: portalType,
    };
    if (force) {
      payload.force_sync = true;
      payload.force_sync_client_ids = ids;
    }
    syncClientNoticesData({
      payload,
      cb: () => {
        setSyncNoticesLoading(false);
        toast.info("Process of fetching notices is started");
        setSelectedClient(null);

        // Refresh client details to get updated is_processing status
        fetchClientDetails();
      },
    });
  };

  const initiateClientLogin = () => {
    setSyncNoticesLoading(true);
    syncClientLoginData({
      payload: {
        client_name: clientId,
      },
      cb: (data) => {
        setSyncNoticesLoading(false);
        toast.info("Processing started");

        // Refresh client details to get updated is_processing status
        fetchClientDetails();
      },
    });
  };

  const handleOk = (forceSyncClientIds = []) => {
    setLoaderText("Fetch Notices");
    setFetchNoticeModal(false);
    // The popup hands back the explicit selection; treat any selection
    // as a force sync so the 48-hour cooldown is bypassed for this row.
    const ids = forceSyncClientIds.length > 0 ? forceSyncClientIds : [clientId];
    syncClientNotices(ids, true);
  };

  const handleCancel = () => {
    setFetchNoticeModal(false);
  };

  const handlLogineOk = () => {
    setLoaderText("Queueing Selected Clients for Processing");
    setInitiateLoginModal(false);
    initiateClientLogin();
  };

  const handleDeleteCancel = () => {
    setInitiateDeleteModal(false);
  };

  const handleDeleteOk = () => {
    handleDeleteClient();
    setInitiateDeleteModal(false);
  };

  const handleDisableCancel = () => {
    setDisableClientModal(false);
  };

  const handleDisableOk = () => {
    handleDisableclient();
    setDisableClientModal(false);
  };

  const handleLoginCancel = () => {
    setInitiateLoginModal(false);
  };

  const formatDateTime = (raw) => {
    if (!raw) return ""; // null, undefined, or empty string
    const date = new Date(raw);
    if (isNaN(date)) return "";

    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    return date.toLocaleString("en-US", options).replace(",", " -");
  };

  const handleDateChange = (e) => {
    const { value } = e.target;

    const updatedFormData = {
      ...formData,
      dob: value ? moment(value).format("YYYY-MM-DD") : null,
    };
    setFormData(updatedFormData);
    setIsFormChanged(
      JSON.stringify(updatedFormData) !== JSON.stringify(initialFormData)
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setIsFormChanged(
      JSON.stringify(updatedFormData) !== JSON.stringify(initialFormData)
    );
  };

  const handleSave = async () => {
    // GST clients must carry a 15-char GSTIN. Surface the error early
    // so the user doesn't lose the in-progress edit to a backend 400.
    if (serviceType === "gstin") {
      const g = (formData?.gstin || "").trim();
      if (!g) {
        toast.error("GSTIN is required for GST clients.");
        return;
      }
      if (g.length !== 15) {
        toast.error("GSTIN must be exactly 15 characters.");
        return;
      }
    }

    setSaveClientLoading(true);

    // Build update payload — only fields that backend accepts
    const payload = {};
    const allowedFields = {
      "income-tax": ["client_name", "username", "password", "portal_name_of_assessee", "dob", "disabled", "read_only", "assigned_user_ids"],
      gstin: ["client_name", "gst_username", "password", "gstin", "portal_name_of_assessee", "disabled", "read_only", "assigned_user_ids"],
      tds: ["client_name", "tds_username", "password", "tan_number", "tan_password", "portal_name_of_assessee", "dob", "disabled", "read_only", "assigned_user_ids"],
    };
    const fields = allowedFields[serviceType] || [];
    fields.forEach((f) => {
      // assigned_user_ids (many-to-many): an empty array is how an admin
      // moves a client to the Shared pool. The default whitelist drops
      // null/"" / undefined which would silently no-op a "set to Shared"
      // pick. Treat this field as explicit-pass-through: undefined (never
      // touched) is dropped, but an explicit array (including []) IS
      // forwarded.
      if (f === "assigned_user_ids") {
        if (formData[f] !== undefined) {
          payload[f] = formData[f];
        }
        return;
      }
      if (formData[f] !== undefined && formData[f] !== null && formData[f] !== "") {
        payload[f] = formData[f];
      }
    });

    try {
      const response = await updateData(`${getClientApiBase()}/${clientId}`, payload);

      toast.info("Client Info Updated Successfully");
      setInitialFormData(response || formData);
      setFormData(response || formData);
      setIsEditingClient(false);
    } catch (err) {
      // `updateData` throws `error.response?.data` (the FastAPI body, e.g.
      // `{detail: "..."}` on 4xx) or a fallback string. Surface the BE
      // detail so users see real errors like "GST username must be
      // unique." instead of the generic "Failed to save client". Same
      // pattern used by admin/* edit drawers.
      toast.error(
        typeof err === "string"
          ? err
          : err?.detail || err?.message || "Failed to save client"
      );
      setFormData(initialFormData);
    } finally {
      if (isEditingClient) {
        setIsEditingClient(false);
      }
      setSaveClientLoading(false);
      setIsFormChanged(false);
    }
  };

  const toggleCredentialsEdit = () => {
    setEditingCredentials(!editingCredentials);
    // if (editingCredentials) {
    //   setFormData(initialFormData);
    // }
  };

  // Function to periodically check client status when processing
  useEffect(() => {
    let intervalId;

    // Truthy check — FastAPI returns bool `true`, legacy Frappe returned int `1`.
    // `=== 1` here missed the bool path, so the spinner/polling never started
    // on the FastAPI stack despite the fetch actually running.
    if (formData?.is_processing) {
      intervalId = setInterval(() => {
        fetchClientDetails();

        if (serviceType == "income-tax") {
          getNoticesCount("Response to Outstanding Demand");
          getNoticesCount("E Proceeding");
        } else if (serviceType == "gstin") {
          getNoticesCount("GST Notice And Order");
          getNoticesCount("GST Additional Notice And Order");
        } else {
          getNoticesCount("Quarter");
        }
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [formData?.is_processing, clientId]);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  // useEffect(() => {
  //   setInitialFormData(formData);
  // }, []);

  useEffect(() => {
    if (serviceType == "income-tax") {
      getNoticesCount("Response to Outstanding Demand");
      getNoticesCount("E Proceeding");
    } else if (serviceType == "gstin") {
      getNoticesCount("GST Notice And Order");
      getNoticesCount("GST Additional Notice And Order");
    } else {
      getNoticesCount("Quarter");
    }
  }, []);

  return (
    <>
      <div className="flex items-center justify-between w-full">
        <Breadcrumb
          items={[
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{
                    pathname: "/",
                  }}
                >
                  Home
                </TenantLink>
              ),
            },
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{
                    pathname:
                      serviceType == "gstin"
                        ? "/gstin"
                        : serviceType == "tds"
                        ? "/tds"
                        : "/income-tax",
                  }}
                >
                  {serviceType == "gstin"
                    ? "GST "
                    : serviceType == "tds"
                    ? "TDS"
                    : "Income Tax"}
                </TenantLink>
              ),
            },
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{
                    pathname:
                      serviceType == "gstin"
                        ? "/gstin/clients"
                        : serviceType == "tds"
                        ? "/tds/clients"
                        : "/income-tax/clients",
                  }}
                >
                  Clients{" "}
                </TenantLink>
              ),
            },
            { title: `${formData?.client_name || "Client Details"}` },
          ]}
          style={{
            marginBottom: "10px",
            color: "#151c40",
          }}
        />
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle title="Client Details" />
      <div className="max-w-3xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-8">
        {serviceType === ServiceType.INCOME_TAX && (
          <div className="flex justify-between">
            {formData?.is_processing ? (
              <motion.div
                className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-2 font-semibold py-0 h-[40px] shadow-sm flex items-center gap-2 animate-pulse"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <SyncOutlined spin className="text-blue-600" />
                <span>Syncing Notices...</span>
              </motion.div>
            ) : (
              <div></div>
            )}
            <div className="flex justify-end mb-6 gap-4">
              {formData.disabled == 0 && (
                <div className="flex items-center gap-4">
                  <Button
                    type="primary"
                    onClick={() => setFetchNoticeModal(true)}
                  >
                    <FaArrowsRotate /> Fetch Notices
                  </Button>
                </div>
              )}
              {formData.disabled == 0 && (
                <div className="flex items-center gap-4">
                  <Popover
                    placement="bottom"
                    content={
                      <div className="flex flex-col p-0">
                        <p
                          onClick={() =>
                            navigate(
                              `/${serviceType}/add-eproceedings/${clientId}`
                            )
                          }
                          className="flex gap-2  items-center border-solid border-b-[2px] p-2 border-gray-300 text-gray-500 font-normal hover:font-semibold cursor-pointer"
                        >
                          <FaPlus /> Add E Proceeding
                        </p>
                        <p
                          onClick={() => {
                            navigate(
                              `/${serviceType}/add-responseoutstandings/${clientId}`
                            );
                          }}
                          className="p-2 flex gap-2  items-center text-gray-500 font-normal hover:font-semibold cursor-pointer"
                        >
                          <FaPlus /> Add Response To Outstanding Demand
                        </p>
                      </div>
                    }
                  >
                    <Button type="primary">
                      Add Notices Manually <FaAngleDoubleDown />
                    </Button>
                  </Popover>
                </div>
              )}
              {isEditingClient ? (
                <>
                  <Button
                    onClick={() => {
                      setIsEditingClient(false);
                      setIsFormChanged(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    loading={saveClientLoading}
                    onClick={handleSave}
                    disabled={!isFormChanged}
                  >
                    Save Client
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={() => setIsEditingClient(true)}>
                  <EditOutlined /> Edit Client
                </Button>
              )}
            </div>
          </div>
        )}

        {serviceType === ServiceType.GSTIN && (
          <>
            <div className="flex justify-between">
              {formData?.is_processing ? (
                <motion.div
                  className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-2 font-semibold py-0 h-[40px] shadow-sm flex items-center gap-2 animate-pulse"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <SyncOutlined spin className="text-blue-600" />
                  <span>Syncing Notices...</span>
                </motion.div>
              ) : (
                <div></div>
              )}
              <div className="flex justify-end mb-6 gap-4">
                {formData.disabled == 0 && (
                  <div className="flex items-center gap-4">
                    <Button
                      type="primary"
                      onClick={() => setFetchNoticeModal(true)}
                    >
                      <FaArrowsRotate /> Fetch Notices
                    </Button>
                  </div>
                )}
                {formData.disabled == 0 && (
                  <div className="flex items-center gap-4">
                    <Popover
                      placement="bottom"
                      content={
                        <div className="flex flex-col p-0">
                          <p
                            onClick={() =>
                              navigate(
                                `/${serviceType}/add-notices/${clientId}`
                              )
                            }
                            className="p-2 flex gap-2  items-center text-gray-500 font-normal hover:font-semibold cursor-pointer"
                          >
                            <FaPlus /> Add GST Notice And Order
                          </p>
                          <p
                            onClick={() =>
                              navigate(
                                `/${serviceType}/add-additional-notices/${clientId}`
                              )
                            }
                            className="p-2 flex gap-2  items-center text-gray-500 font-normal hover:font-semibold cursor-pointer"
                          >
                            <FaPlus /> Add GST Additional Notice
                          </p>
                        </div>
                      }
                    >
                      <Button type="primary">
                        Add Notice Manually <FaAngleDoubleDown />
                      </Button>
                    </Popover>
                  </div>
                )}
                {isEditingClient ? (
                  <>
                    <Button
                      onClick={() => {
                        setIsEditingClient(false);
                        setIsFormChanged(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      loading={saveClientLoading}
                      onClick={handleSave}
                      disabled={!isFormChanged}
                    >
                      Save Client
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => setIsEditingClient(true)}
                  >
                    <EditOutlined /> Edit Client
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {serviceType === ServiceType.TDS && (
          <>
            <div className="flex justify-between">
              {formData?.is_processing ? (
                <motion.div
                  className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-2 font-semibold py-0 h-[40px] shadow-sm flex items-center gap-2 animate-pulse"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <SyncOutlined spin className="text-blue-600" />
                  <span>Syncing Notices...</span>
                </motion.div>
              ) : (
                <div></div>
              )}
              <div className="flex justify-end mb-6 gap-4">
                {formData.disabled == 0 && (
                  <div className="flex items-center gap-4">
                    <Button
                      type="primary"
                      onClick={() => setFetchNoticeModal(true)}
                    >
                      <FaArrowsRotate /> Fetch Notices
                    </Button>
                  </div>
                )}
                {formData.disabled == 0 && (
                  <div className="flex items-center gap-4">
                    <Popover
                      placement="bottom"
                      content={
                        <div className="flex flex-col p-0">
                          <p
                            onClick={() =>
                              navigate(
                                `/${serviceType}/add-notices/${clientId}`
                              )
                            }
                            className="p-2 flex gap-2  items-center text-gray-500 font-normal hover:font-semibold cursor-pointer"
                          >
                            <FaPlus /> Add TDS Notice
                          </p>
                        </div>
                      }
                    >
                      <Button type="primary">
                        Add Notice Manually <FaAngleDoubleDown />
                      </Button>
                    </Popover>
                  </div>
                )}
                {isEditingClient ? (
                  <>
                    <Button
                      onClick={() => {
                        setIsEditingClient(false);
                        setIsFormChanged(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      loading={saveClientLoading}
                      onClick={handleSave}
                      disabled={!isFormChanged}
                    >
                      Save Client
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => setIsEditingClient(true)}
                  >
                    <EditOutlined /> Edit Client
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Buttons */}
        {serviceType === ServiceType.INCOME_TAX && (
          <>
            <div className="flex justify-between mb-6">
              <div className="flex items-center gap-4">
                <Badge count={eproceedingsCount + responseOutstandingsCount} style={{ zIndex: "10" }}>
                  <Button
                    type="primary"
                    className="exempt-from-readonly"
                    onClick={() =>
                      navigate(
                        `/${serviceType}/eproceedings/${clientId}?noticeType=${
                          formData.disabled == 1 ? "inactive" : "active"
                        }&client_name=${formData?.client_name}`
                      )
                    }
                  >
                    Income Tax Notices
                  </Button>
                </Badge>
              </div>
            </div>
          </>
        )}

        {serviceType === ServiceType.GSTIN && (
          <>
            <div className="flex justify-between mb-6">
              <div className="flex items-center gap-4">
                <Badge
                  count={gstNoticesCount + additionalNoticesCount}
                  style={{ zIndex: "10" }}
                >
                  <Button
                    type="primary"
                    className="exempt-from-readonly"
                    onClick={() =>
                      navigate(
                        `/${serviceType}/notices/${clientId}?disabledType=${
                          formData.disabled == 0 ? "active" : "inactive"
                        }&client_name=${formData?.client_name}`
                      )
                    }
                  >
                    GST Notices
                  </Button>
                </Badge>
              </div>
            </div>
          </>
        )}

        {serviceType === ServiceType.TDS && (
          <div className="flex justify-between mb-6">
            <Badge count={tdsNoticeCount} style={{ zIndex: "10" }}>
              <Button
                type="primary"
                className="exempt-from-readonly"
                onClick={() =>
                  navigate(
                    `/${serviceType}/notices/${clientId}?disabledType=${
                      formData.disabled == 0 ? "active" : "inactive"
                    }&client_name=${formData?.client_name}`
                  )
                }
              >
                TDS Notices
              </Button>
            </Badge>
          </div>
        )}

        {/* Client Details */}
        <div className="border-b pb-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Client Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-600 text-sm">
                Client Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="client_name"
                readOnly={!isEditingClient || formData.disabled == 1}
                value={formData.client_name}
                onChange={handleInputChange}
                className="w-full p-2 border rounded bg-gray-100 text-gray-800 font-semibold"
              />
            </div>
            {serviceType != ServiceType.GSTIN &&
              serviceType != ServiceType.TDS && (
                <div>
                  <label className="text-gray-600 text-sm">DOB</label>

                  <Input
                    type="date"
                    disabled={!isEditingClient || formData.disabled == 1}
                    name="dob"
                    style={{ color: "#111" }}
                    value={formData.dob}
                    onChange={(e) => handleDateChange(e)}
                    className="w-full"
                  />
                </div>
              )}

            {serviceType == ServiceType.TDS && (
              <div>
                <label className="text-gray-600 text-sm">
                  TAN <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="tan_number"
                  readOnly={!isEditingClient || formData.disabled == 1}
                  value={formData.tan_number}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded bg-gray-100 text-gray-800 font-semibold"
                />
              </div>
            )}
            {serviceType == ServiceType.GSTIN && (
              <div>
                <label className="text-gray-600 text-sm">
                  GSTIN <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="gstin"
                  readOnly={!isEditingClient || formData.disabled == 1}
                  value={formData.gstin}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded bg-gray-100 text-gray-800 font-semibold"
                />
              </div>
            )}
          </div>
        </div>

        {/* Income Tax Login Credentials */}
        <div className="border-b pb-4 mb-4">
          <div className="flex gap-2">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              Login Credentials
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-600 text-sm">
                Username <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                readOnly={!isEditingClient || formData.disabled == 1}
                name={
                  serviceType === ServiceType.GSTIN
                    ? "gst_username"
                    : serviceType === ServiceType.TDS
                    ? "tds_username"
                    : "username"
                }
                onChange={handleInputChange}
                value={
                  serviceType === ServiceType.GSTIN
                    ? formData.gst_username
                    : serviceType === ServiceType.TDS
                    ? formData?.tds_username
                    : formData?.username
                }
                className={`w-full p-2 border rounded ${
                  editingCredentials ? `bg-white` : `bg-gray-100`
                } font-semibold text-gray-800`}
              />
            </div>
            <div>
              <label className="text-gray-600 text-sm">
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                type="password"
                readOnly={!isEditingClient || formData.disabled == 1}
                onChange={handleInputChange}
                name="password"
                value={formData?.password || ""}
                placeholder={isEditingClient ? "Leave blank to keep current password" : "********"}
                className={`w-full p-2 border rounded ${
                  editingCredentials ? `bg-white` : `bg-gray-100`
                } font-semibold text-gray-800`}
              />
            </div>
          </div>
        </div>

        {/* Sync Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2 flex items-center gap-2">
            Sync Details{" "}
          </h2>
          <div>
            <label className="text-gray-600 text-sm flex gap-2 mb-2">
              Last Sync
            </label>
            <Input
              type="text"
              name={
                serviceType === ServiceType.GSTIN
                  ? "last_gst_sync"
                  : serviceType === ServiceType.TDS
                  ? "last_tds_sync"
                  : "last_income_tax_sync"
              }
              value={formatDateTime(
                serviceType === ServiceType.GSTIN
                  ? formData.last_gst_sync
                  : serviceType === ServiceType.TDS
                  ? formData?.last_tds_sync
                  : formData?.last_income_tax_sync
              )}
              disabled
              className="w-full p-2 border rounded bg-gray-100 text-gray-800"
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">{formData.timezone}</p>
        </div>

        {/* Whitebooks Session — GST only, self-gates on
            `gst_default_fetch_method == "whitebooks_api"` via the BE
            status endpoint's `method_active` flag (returns null when
            inactive). Sits between Sync Details and the action row
            so an unauthenticated client is impossible to miss. */}
        {serviceType === ServiceType.GSTIN && clientId && (
          <WhitebooksSessionCard clientId={clientId} />
        )}

        <div className="flex items-center  justify-end space-x-2 mt-4">
          <div className="flex items-center gap-2">
            <Button
              danger={formData?.disabled == 0 ? true : false}
              loading={deleteClientLoading}
              onClick={() => setDisableClientModal(true)}
            >
              {formData?.disabled ? "Activate Client" : "Disable Client"}
            </Button>
          </div>
          {/* <Button
            danger
            loading={deleteClientLoading}
            onClick={() => setInitiateDeleteModal(true)}
          >
            <DeleteOutlined /> Delete Client
          </Button> */}
        </div>
      </div>

      <FetchNoticesConfirmModal
        open={fetchNoticeModal}
        onCancel={handleCancel}
        onConfirm={handleOk}
        portal={serviceType}
        clientIds={clientId ? [clientId] : undefined}
      />

      <Modal
        title="Confirmation"
        open={initiateLDeleteModal}
        onOk={handleDeleteOk}
        onCancel={handleDeleteCancel}
        centered
        style={{ top: -50 }}
        zIndex={99999}
      >
        <div className="flex items-center justify-center flex-col text-center">
          <img
            src={WarningImage}
            width={250}
            height={250}
            className="rounded-lg"
          />
          <p className="text-lg mt-4">Are you sure you want to Delete ? </p>
        </div>
      </Modal>

      <Modal
        title="Confirmation"
        open={disableClientModal}
        onOk={handleDisableOk}
        onCancel={handleDisableCancel}
        centered
        style={{ top: -50 }}
        zIndex={99999}
      >
        <div className="flex items-center justify-center flex-col text-center">
          <img
            src={WarningImage}
            width={250}
            height={250}
            className="rounded-lg"
          />
          <p className="text-lg mt-4">
            Are you sure you want to{" "}
            <strong>{formData?.disabled == 0 ? "Disable" : "Activate"}</strong>{" "}
            Client ?{" "}
          </p>
        </div>
      </Modal>

      <Modal
        title="Confirmation"
        open={initiateLoginModal}
        onOk={handlLogineOk}
        onCancel={handleLoginCancel}
        centered
        style={{ top: -50 }}
        zIndex={99999}
      >
        <div className="flex items-center justify-center flex-col text-center">
          <img
            src={WarningImage}
            width={250}
            height={250}
            className="rounded-lg"
          />
          <p className="text-lg mt-4">Are you sure you want to Login ? </p>
        </div>
      </Modal>

      <UniversalLoader show={syncNoticeLoading} text={loaderText} />

      {/* Whitebooks inline OTP retry modal — opens when a GST single-client
          fetch attempt pre-flighted and discovered no live Whitebooks
          session for this client. The card inside drives Authenticate +
          OTP submission; on success it fires onSessionReady, we close the
          modal and re-invoke syncClientNotices with the preserved force flag. */}
      <Modal
        title="Authenticate this client"
        open={Boolean(whitebooksRetryClientId)}
        onCancel={() => setWhitebooksRetryClientId(null)}
        footer={null}
        destroyOnClose
        width={520}
      >
        {whitebooksRetryClientId && (
          <WhitebooksSessionCard
            clientId={whitebooksRetryClientId}
            compact
            onSessionReady={() => {
              const id = whitebooksRetryClientId;
              const force = whitebooksRetryForce;
              setWhitebooksRetryClientId(null);
              setWhitebooksRetryForce(false);
              syncClientNotices(id, force);
            }}
            onClose={() => setWhitebooksRetryClientId(null)}
          />
        )}
      </Modal>
    </>
  );
};
